import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRefresh } from '../RefreshContext';
import { BACKEND_URL } from '../config';
import OrderEdit from './OrderEdit';
import OrderProducts from './OrderProducts';
import Preorder from './Preorder';
import OrderTotal from './OrderTotal';
import OrderSummary from './OrderSummary';
import { useLanguage } from '../contexts/LanguageContext';
import PrintTicket from './PrintTicket';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { printItemTicket } from '../utils/receiptPrinter';
import { useSystemFeatures } from '../hooks/useSystemFeatures';

// Area label mapping for ticket headers
const AREA_LABELS = { kitchen: 'COCINA', bar: 'BAR', pizza: 'PIZZA', other: 'OTROS' };

export default function OrderScreen({ orderId, theme, onClose, user, tableName, orderName, allOptions }) {
    const { t } = useLanguage();
        const [orderItems, setOrderItems] = useState([]);

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [preorderItems, setPreorderItems] = useState([]);
    const [parentOrderName, setParentOrderName] = useState('');
    const {refreshKey, refresh } = useRefresh();
    const printRef = useRef();
    const containerRef = useRef(null);
    const layoutMode = useLayoutMode(containerRef);
    const [kitchenOutputMode, setKitchenOutputMode] = useState('display');
    const [printDontMakeItems, setPrintDontMakeItems] = useState(false);
    const { orderLabelsEnabled, customItemsEnabled } = useSystemFeatures();
    const [orderLabel, setOrderLabel] = useState('');
    const [savingLabel, setSavingLabel] = useState(false);

    // Fetch kitchen output settings once on mount
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/system-settings`)
            .then(res => res.ok ? res.json() : {})
            .then(data => {
                if (data.kitchenOutputMode) setKitchenOutputMode(data.kitchenOutputMode);
                if (data.printDontMakeItems !== undefined) setPrintDontMakeItems(data.printDontMakeItems === true || data.printDontMakeItems === 'true');
            })
            .catch(() => {});
    }, []);

    // Save the order label on its own endpoint, which skips the validation of
    // the full order PUT. An empty value clears the label.
    const saveOrderLabel = useCallback(async (label) => {
        if (!orderId) return;
        setSavingLabel(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/label`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label })
            });
            if (!response.ok) throw new Error(t('error') + ': Failed to save order label');
            setOrder(prev => (prev ? { ...prev, order_label: label } : prev));
        } catch (err) {
            console.error('Error saving order label:', err);
        } finally {
            setSavingLabel(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    // Print item tickets grouped by area (fire-and-forget)
    const printItemTickets = useCallback((printData) => {
        if (!printData || !printData.items) return;
        const items = printDontMakeItems
            ? printData.items
            : printData.items.filter(i => !i.dontMake);
        if (items.length === 0) return;

        // Group items by area
        const groups = {};
        items.forEach(item => {
            const area = item.area || 'other';
            if (!groups[area]) groups[area] = [];
            groups[area].push(item);
        });

        // Print one ticket per area
        const userName = user?.user_name || '';
        Object.entries(groups).forEach(([area, areaItems]) => {
            const label = AREA_LABELS[area] || area.toUpperCase();
            printItemTicket(areaItems, printData.orderName, printData.tableName, label, userName).catch(() => {});
        });
    }, [printDontMakeItems, user]);

    useEffect(() => {
        fetchOrderItems();
        async function fetchOrder() {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch order');
                const data = await response.json();
                setOrder(data);
                setOrderLabel(data.order_label || '');

                if (data.parent_tab_id) {
                    try {
                        const parentResponse = await fetch(`${BACKEND_URL}/api/orders/${data.parent_tab_id}`);
                        if (parentResponse.ok) {
                            const parentOrder = await parentResponse.json();
                            if (parentOrder.name) {
                                setParentOrderName(parentOrder.name.substring(0, 5));
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching parent tab name:', err);
                    }
                } else {
                    setParentOrderName('');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        if (orderId) fetchOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, refreshKey]);

        const fetchOrderItems = async () => {
            if (!orderId) {
                setLoading(false);
                return;
            }
    
            try {
                setLoading(true);
                const response = await fetch(`${BACKEND_URL}/api/order-products?order_id=${orderId}`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch order items');
                const data = await response.json();
                setOrderItems(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

    useEffect(() => {
        async function fetchProducts() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/products`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch products');
                const data = await response.json();
                setProducts(data);
            } catch (err) {
                setProducts([]);
            }
        }

        async function fetchCategories() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/categories`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch categories');
                const data = await response.json();
                setCategories(data);
            } catch (err) {
                setCategories([]);
            }
        }

        fetchProducts();
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for refresh events from OrderSummary
    useEffect(() => {
        const handleRefresh = () => {
            fetchOrderItems();
        };
        window.addEventListener('refreshOrderItems', handleRefresh);
        return () => window.removeEventListener('refreshOrderItems', handleRefresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    // Handle close with preorder items check
    const handleClose = () => {
        if (preorderItems.length > 0) {
            if (window.confirm(t('confirmLeaveWithPreorderItems'))) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    // Handle browser back button - should go to ServerScreen, not login
    useEffect(() => {
        // Push a new history entry when OrderScreen mounts
        window.history.pushState({ orderScreen: true }, '');

        const handlePopState = (event) => {
            // Intercept browser back button and call handleClose instead
            if (onClose) {
                handleClose();
                // Push state again to keep user on /pos route
                window.history.pushState({ orderScreen: true }, '');
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClose, preorderItems]);

    // Listen for navigation requests (Gibbons logo, etc.) and confirm if preorder items exist
    useEffect(() => {
        const handleNavigateRequest = (event) => {
            if (preorderItems.length > 0) {
                if (!window.confirm(t('confirmLeaveWithPreorderItems'))) {
                    event.preventDefault(); // Prevent the navigation
                }
            }
        };

        window.addEventListener('requestNavigateAway', handleNavigateRequest);

        return () => {
            window.removeEventListener('requestNavigateAway', handleNavigateRequest);
        };
    }, [preorderItems, t]);

    if (!orderId) return null;

    if (editing && order) {
        return (
            <OrderEdit
                order={order}
                theme={theme}
                user={user}
                onSave={async (updatedOrder) => {
                    try {
                        const res = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(updatedOrder)
                        });
                        if (!res.ok) throw new Error(t('error') + ': Failed to save order');
                        setOrder(updatedOrder);
                        setEditing(false);
                        refresh(); // Refresh all other components
                    } catch (err) {
                        alert(t('error') + ': ' + err.message);
                    }
                }}
                onCancel={() => setEditing(false)}
                onDelete={() => {
                    setEditing(false);
                    if (onClose) onClose();
                }}
            />
        );
    }

    // Check for existing item by id and selectedOptions
    const isSameItem = (itemA, itemB) => {
        if (itemA.id !== itemB.id) return false;
        // Compare selectedOptions (shallow compare by option id if present)
        if (itemA.selectedOptions && itemB.selectedOptions) {
            // If selectedOptions is an object with option property
            if (itemA.selectedOptions.option && itemB.selectedOptions.option) {
                return itemA.selectedOptions.option.id === itemB.selectedOptions.option.id;
            }
            // If selectedOptions is an array
            if (Array.isArray(itemA.selectedOptions) && Array.isArray(itemB.selectedOptions)) {
                if (itemA.selectedOptions.length !== itemB.selectedOptions.length) return false;
                for (let i = 0; i < itemA.selectedOptions.length; i++) {
                    if (itemA.selectedOptions[i].id !== itemB.selectedOptions[i].id) return false;
                }
                return true;
            }
        }
        // If no selectedOptions, treat as same item
        if (!itemA.selectedOptions && !itemB.selectedOptions) return true;
        return false;
    };

    // A custom item arrives with its own quantity and a unique local id, so it
    // is always a new preorder line and never merges with another
    const handleAddCustomItem = customItemsEnabled
        ? (customItem) => setPreorderItems(items => [...items, customItem])
        : undefined;

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                maxWidth: '100vw',
                padding: '1rem',
                background: theme?.colors?.background || '#f5f5f5',
                color: theme?.colors?.text || '#222',
                boxSizing: 'border-box',
                overflowX: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '100vh'
            }}
        >
            <style>{`
                /* Mobile layout (default and fallback) */
                .order-layout {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 0.5rem;
                    row-gap: 0.5rem;
                    background: ${theme?.colors?.surface || '#fff'};
                    border-radius: ${theme?.borderRadius || 8}px;
                    box-shadow: ${theme?.boxShadow || '0 2px 8px rgba(0,0,0,0.1)'};
                    border: 2px solid ${theme?.colors?.primary || '#1976d2'};
                    box-sizing: border-box;
                    overflow-x: hidden;
                    min-height: 200px;
                }

                .order-header {
                    width: 100%;
                    box-sizing: border-box;
                    overflow: visible;
                    padding: 0.5rem;
                    margin-bottom: 0.5rem;
                    background: ${theme?.colors?.surface || '#fff'};
                }

                /* Use media queries instead of container queries for iPad compatibility */
                @media (max-width: 767px) {
                    .order-layout {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        max-width: min(600px, 100%);
                        margin: 0;
                        padding: 0rem;
                        row-gap: 0rem;
                        background: ${theme?.colors?.surface || '#fff'};
                        border-radius: ${theme?.borderRadius || 8}px;
                        box-shadow: ${theme?.boxShadow || '0 2px 8px #0002'};
                        border: 2px solid ${theme?.colors?.primary || '#1976d2'};
                        box-sizing: border-box;
                        overflow-x: hidden;
                    }

                    .order-header {
                        width: 100%;
                        box-sizing: border-box;
                        overflow: hidden;
                        padding: 0.25rem;
                        margin-bottom: 0rem;
                    }
                }

                @media (min-width: 768px) and (max-width: 1199px) {
                    .order-layout {
                        display: grid;
                        grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
                        grid-template-rows: auto 1fr;
                        column-gap: 0.5rem;
                        row-gap: 0rem;
                        max-width: min(1200px, 100%);
                        margin: 0;
                        width: 100%;
                        padding: 0;
                        box-sizing: border-box;
                    }

                    .order-header {
                        grid-column: 1 / -1;
                        padding: 0.5rem;
                        background: ${theme?.colors?.surface || '#fff'};
                        border-radius: ${theme?.borderRadius || 8}px;
                        box-shadow: ${theme?.boxShadow || '0 2px 8px #0002'};
                        border: 2px solid ${theme?.colors?.primary || '#1976d2'};
                        max-height: none;
                        overflow: visible;
                        margin-bottom: 0;
                    }

                    .order-left {
                        grid-column: 1;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        min-width: 0;
                        overflow-x: hidden;
                    }

                    .order-right {
                        grid-column: 2;
                        position: sticky;
                        top: 1rem;
                        max-height: calc(100vh - 2rem);
                        overflow-y: auto;
                        overflow-x: hidden;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        min-width: 0;
                    }
                }

                @media (min-width: 1200px) {
                    .order-layout {
                        display: grid;
                        grid-template-columns: minmax(0, 1fr) minmax(0, 2.5fr) minmax(0, 2fr);
                        grid-template-rows: auto 1fr;
                        column-gap: 0.5rem;
                        row-gap: 0.25rem;
                        max-width: min(1600px, 100%);
                        margin: 0;
                        width: 100%;
                        padding: 0;
                        box-sizing: border-box;
                    }

                    .order-header {
                        grid-column: 1 / -1;
                        grid-row: 1;
                        padding: 0.5rem;
                        background: ${theme?.colors?.surface || '#fff'};
                        border-radius: ${theme?.borderRadius || 8}px;
                        box-shadow: ${theme?.boxShadow || '0 2px px #0002'};
                        border: 2px solid ${theme?.colors?.primary || '#1976d2'};
                        max-height: none;
                        overflow: visible;
                        margin-bottom: 0px;
                    }

                    .order-categories {
                        grid-column: 1;
                        grid-row: 2;
                        position: sticky;
                        top: 1rem;
                        align-self: start;
                        max-height: calc(100vh - 2rem);
                        overflow-y: auto;
                        overflow-x: hidden;
                        min-width: 0;
                    }

                    .order-products-wrapper {
                        grid-column: 2;
                        grid-row: 2;
                        max-height: calc(100vh - 2rem);
                        overflow-y: auto;
                        overflow-x: hidden;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        min-width: 0;
                    }

                    .order-summary-total {
                        grid-column: 3;
                        grid-row: 2;
                        position: sticky;
                        top: 1rem;
                        align-self: start;
                        max-height: calc(100vh - 2rem);
                        overflow-y: auto;
                        overflow-x: hidden;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        min-width: 0;
                    }
                }
            `}</style>
            <div className="order-layout">
            {loading && !order ? (
                <div style={{ padding: '2rem', fontSize: '1.2rem', textAlign: 'center', color: theme?.colors?.text || '#000' }}>
                    {t('loading')} {t('orders').toLowerCase()}...
                </div>
            ) : error ? (
                <div style={{ padding: '2rem', fontSize: '1.2rem', textAlign: 'center', color: theme?.colors?.error || 'red', background: theme?.colors?.surface || '#fff' }}>
                    {t('error')}: {error}
                </div>
            ) : !order ? (
                <div style={{ padding: '2rem', fontSize: '1.2rem', textAlign: 'center', color: theme?.colors?.text || '#000', background: theme?.colors?.surface || '#fff' }}>
                    No order found.
                </div>
            ) : (
                <>
                    <div className="order-header">
                        <h2 style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', color: theme?.colors?.primary, marginTop: 0, marginBottom: 0, gap: '0.5rem', minWidth: 0 }}>
                            <button
                                onClick={handleClose}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    handleClose();
                                }}
                                style={{
                                    background: theme?.colors?.secondary || '#888',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 8,
                                    padding: '.5rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: 16,
                                    marginRight: 8,
                                    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                                    userSelect: 'none'
                                }}
                            >
                                {t('back')}
                            </button>
                            <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>{order.name}</span>
                            {parentOrderName && <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary, wordWrap: 'break-word', overflowWrap: 'break-word' }}> ({t('from')} {parentOrderName})</span>}
                            <span style={{ color: theme?.colors?.secondary || '#888', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                table: {order.table_name}
                            </span>
                            <button
                                title={t('edit') + ' ' + t('orders')}
                                onClick={() => setEditing(true)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    marginLeft: 8,
                                    color: theme?.colors?.primary || '#1976d2',
                                    padding: 0
                                }}
                            >
                                ✏️
                            </button>
                        </h2>
                        {orderLabelsEnabled && order.closed === 0 && (
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginTop: '0.5rem',
                                flexWrap: 'wrap',
                                fontSize: '0.9rem',
                                color: theme?.colors?.text || '#000'
                            }}>
                                {t('orderLabel')}:
                                <input
                                    type="text"
                                    value={orderLabel}
                                    maxLength={64}
                                    onChange={(e) => setOrderLabel(e.target.value)}
                                    onBlur={(e) => saveOrderLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={t('orderLabelPlaceholder')}
                                    style={{
                                        flex: '1 1 160px',
                                        minWidth: 0,
                                        maxWidth: '260px',
                                        padding: '0.4rem 0.5rem',
                                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                        borderRadius: theme?.borderRadius || 4,
                                        fontSize: '0.9rem',
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000'
                                    }}
                                />
                                {savingLabel && (
                                    <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#666' }}>
                                        {t('saving')}
                                    </span>
                                )}
                            </label>
                        )}
                    </div>
                    {/* <div><strong>Name:</strong> {order.name ?? 'N/A'}</div>
                    <div><strong>{t('orderNumber')}:</strong> {order.id ?? 'N/A'}</div>
                    <div><strong>Table:</strong> {order.table_name ?? 'N/A'}</div>
                    <div><strong>{t('status')}:</strong> {order.closed === 0 ? t('open') : t('closed')}</div>
                    <div><strong>Items:</strong> {Array.isArray(order.items) ? order.items.join(', ') : (order.items || 'None')}</div>
                    <div> <br /></div> */}
                    {order.closed === 1 ? (
                        <>
                            <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 8,
                    border: '2px dashed #ccc'
                }}>
                    <h4 style={{
                        margin: '0 0 1rem 0',
                        color: theme?.colors?.primary,
                        textAlign: 'center'
                    }}>
                        {t('printPreview')}
                    </h4>
                    {/* Calculate subtotal, discount, and total for print preview */}
                    {(() => {
                        const subtotal = orderItems.reduce((sum, item) => sum + (Number(item.price || 0) * item.quantity), 0);
                        const discount = orderItems.reduce((sum, item) => sum + Number(item.discount || 0), 0);
                        const total = subtotal - discount;
                        return (
                            <PrintTicket
                                ref={printRef}
                                items={orderItems}
                                orderId={orderId}
                                table={{ name: tableName }}
                                tab={{ name: orderName }}
                                theme={theme}
                                allOptions={allOptions}
                                subtotal={subtotal}
                                discount={discount}
                                total={total}
                                closedAt={order?.closed_at}
                            />
                        );
                    })()}
                </div>
                            <button
                                style={{
                                    width: '100%',
                                    margin: '1rem 0',
                                    background: theme?.colors?.primary,
                                    color: theme?.colors?.surface,
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 8,
                                    fontWeight: 'bold',
                                    fontSize: '1.1rem',
                                    cursor: 'pointer',
                                    boxShadow: theme?.boxShadow,
                                }}
                                onClick={async () => {
                                    try {
                                        const res = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ ...order, closed: 0 })
                                        });
                                        if (!res.ok) throw new Error(t('error') + ': Failed to reopen order');
                                        const updated = await res.json();
                                        setOrder(updated);
                                    } catch (err) {
                                        alert(t('error') + ': ' + err.message);
                                    }
                                }}
                            >
                                {t('reopen')} {t('order')}
                            </button>
                        </>
                    ) : layoutMode === 'desktop' ? (
                        <>
                            {/* Desktop: OrderProducts renders .order-categories and .order-products-wrapper divs */}
                            <OrderProducts
                                products={products}
                                categories={categories}
                                theme={theme}
                                layoutMode={layoutMode}
                                onAddCustomItem={handleAddCustomItem}
                                onAddProduct={product => {
                                    setPreorderItems(items => {
                                        // Check if product already exists (by id and selectedOptions)
                                        const existingIndex = items.findIndex(item => isSameItem(item, product));
                                        if (existingIndex >= 0) {
                                            // Increment quantity of existing item
                                            return items.map((item, index) =>
                                                index === existingIndex
                                                    ? { ...item, qty: (item.qty || 1) + 1 }
                                                    : item
                                            );
                                        }
                                        // Add new item with qty = 1
                                        return [...items, { ...product, qty: 1 }];
                                    });
                                }}
                            />
                            <div className="order-summary-total">
                                <Preorder
                                    items={preorderItems}
                                    theme={theme}
                                    onRemoveItem={idx => {
                                        setPreorderItems(items => items.filter((_, i) => i !== idx));
                                    }}
                                    onQtyChange={(idx, newQty) => {
                                        setPreorderItems(items => items.map((item, i) =>
                                            i === idx ? { ...item, qty: newQty } : item
                                        ));
                                    }}
                                    onEditItem={(idx, updatedItem) => {
                                        setPreorderItems(items => items.map((item, i) =>
                                            i === idx ? { ...item, ...updatedItem } : item
                                        ));
                                    }}
                                    onSendOrder={async () => {
                                        try {
                                            // Add userId and selected option info to each item before sending
                                            const itemsWithUserId = preorderItems.map(item => {
                                                let selectedOptionId = null;
                                                if (item.selectedOptions && typeof item.selectedOptions === 'object') {
                                                    // selectedOptions is an object like { groupId: option } or { groupId: [options] }
                                                    const optionIds = [];
                                                    Object.values(item.selectedOptions).forEach(selection => {
                                                        if (Array.isArray(selection)) {
                                                            // Checkbox group - collect all selected option ids
                                                            selection.forEach(opt => {
                                                                if (opt && opt.id) {
                                                                    optionIds.push(opt.id);
                                                                }
                                                            });
                                                        } else if (selection && selection.id) {
                                                            // Radio button - single option
                                                            optionIds.push(selection.id);
                                                        }
                                                    });
                                                    if (optionIds.length > 0) {
                                                        selectedOptionId = optionIds;
                                                    }
                                                }
                                                // Always send adjustedPrice, even if 0
                                                return {
                                                    ...item,
                                                    userId: user?.user_id || user?.id || 1,
                                                    selectedOptionId,
                                                    price: item.adjustedPrice !== undefined && item.adjustedPrice !== null ? item.adjustedPrice : item.price
                                                };
                                            });
                                            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/send-items`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ items: itemsWithUserId })
                                            });
                                            if (!response.ok) throw new Error(t('error') + ': Failed to send order');
                                            const result = await response.json();
                                            // Auto-print item tickets if configured
                                            if ((kitchenOutputMode === 'tickets' || kitchenOutputMode === 'both') && result.printData) {
                                                printItemTickets(result.printData);
                                            }
                                            setPreorderItems([]); // Clear preorder items after sending
                                            refresh(); // Trigger global refresh so OrderTotal updates
                                        } catch (err) {
                                            alert(t('error') + ': ' + err.message);
                                        }
                                    }}
                                />
                                <OrderSummary
                                    orderId={orderId}
                                    orderItems={orderItems}
                                    tableName={order.table_name}
                                    orderName={order.name}
                                    parentOrderName={parentOrderName}
                                    allOptions={allOptions}
                                    theme={theme}
                                    user={user}
                                    printRef={printRef}
                                    layoutMode={layoutMode}
                                />
                                <OrderTotal
                                    orderId={orderId}
                                    theme={theme}
                                    orderName={order.name}
                                    tableName={order.table_name}
                                    onClose={onClose}
                                    user={user}
                                    layoutMode={layoutMode}
                                    onAddToPreorder={product => {
                                        setPreorderItems(items => {
                                            // Check if product already exists
                                            const existingIndex = items.findIndex(item => item.id === product.id);
                                            if (existingIndex >= 0) {
                                                // Increment quantity of existing item
                                                return items.map((item, index) =>
                                                    index === existingIndex
                                                        ? { ...item, qty: (item.qty || 1) + 1 }
                                                        : item
                                                );
                                            }
                                            // Add new item
                                            return [...items, { ...product, qty: 1 }];
                                        });
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Mobile/Tablet: Two-column or single-column layout */}
                            <div className={layoutMode === 'tablet' ? 'order-left' : ''}>
                                <OrderProducts
                                    products={products}
                                    categories={categories}
                                    theme={theme}
                                    layoutMode={layoutMode}
                                    onAddCustomItem={handleAddCustomItem}
                                    onAddProduct={product => {
                                        setPreorderItems(items => {
                                            // Check if product already exists (by id and selectedOptions)
                                            const existingIndex = items.findIndex(item => isSameItem(item, product));
                                            if (existingIndex >= 0) {
                                                // Increment quantity of existing item
                                                return items.map((item, index) =>
                                                    index === existingIndex
                                                        ? { ...item, qty: (item.qty || 1) + 1 }
                                                        : item
                                                );
                                            }
                                            // Add new item with qty = 1
                                            return [...items, { ...product, qty: 1 }];
                                        });
                                    }}
                                />
                            </div>
                            <div className={layoutMode === 'tablet' ? 'order-right' : ''}>
                                <Preorder
                                    items={preorderItems}
                                    theme={theme}
                                    onRemoveItem={idx => {
                                        setPreorderItems(items => items.filter((_, i) => i !== idx));
                                    }}
                                    onQtyChange={(idx, newQty) => {
                                        setPreorderItems(items => items.map((item, i) =>
                                            i === idx ? { ...item, qty: newQty } : item
                                        ));
                                    }}
                                    onEditItem={(idx, updatedItem) => {
                                        setPreorderItems(items => items.map((item, i) =>
                                            i === idx ? { ...item, ...updatedItem } : item
                                        ));
                                    }}
                                    onSendOrder={async () => {
                                        try {
                                            // Add userId and selected option info to each item before sending
                                            const itemsWithUserId = preorderItems.map(item => {
                                                let selectedOptionId = null;
                                                if (item.selectedOptions && typeof item.selectedOptions === 'object') {
                                                    // selectedOptions is an object like { groupId: option } or { groupId: [options] }
                                                    const optionIds = [];
                                                    Object.values(item.selectedOptions).forEach(selection => {
                                                        if (Array.isArray(selection)) {
                                                            // Checkbox group - collect all selected option ids
                                                            selection.forEach(opt => {
                                                                if (opt && opt.id) {
                                                                    optionIds.push(opt.id);
                                                                }
                                                            });
                                                        } else if (selection && selection.id) {
                                                            // Radio button - single option
                                                            optionIds.push(selection.id);
                                                        }
                                                    });
                                                    if (optionIds.length > 0) {
                                                        selectedOptionId = optionIds;
                                                    }
                                                }
                                                // Always send adjustedPrice, even if 0
                                                return {
                                                    ...item,
                                                    userId: user?.user_id || user?.id || 1,
                                                    selectedOptionId,
                                                    price: item.adjustedPrice !== undefined && item.adjustedPrice !== null ? item.adjustedPrice : item.price
                                                };
                                            });
                                            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/send-items`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ items: itemsWithUserId })
                                            });
                                            if (!response.ok) throw new Error(t('error') + ': Failed to send order');
                                            const result = await response.json();
                                            // Auto-print item tickets if configured
                                            if ((kitchenOutputMode === 'tickets' || kitchenOutputMode === 'both') && result.printData) {
                                                printItemTickets(result.printData);
                                            }
                                            setPreorderItems([]); // Clear preorder items after sending
                                            refresh(); // Trigger global refresh so OrderTotal updates
                                        } catch (err) {
                                            alert(t('error') + ': ' + err.message);
                                        }
                                    }}
                                />
                                <OrderSummary
                                    orderId={orderId}
                                    orderItems={orderItems}
                                    tableName={order.table_name}
                                    orderName={order.name}
                                    parentOrderName={parentOrderName}
                                    allOptions={allOptions}
                                    theme={theme}
                                    user={user}
                                    printRef={printRef}
                                    layoutMode={layoutMode}
                                />
                                <OrderTotal
                                    orderId={orderId}
                                    theme={theme}
                                    orderName={order.name}
                                    tableName={order.table_name}
                                    onClose={onClose}
                                    user={user}
                                    layoutMode={layoutMode}
                                    onAddToPreorder={product => {
                                        setPreorderItems(items => {
                                            // Check if product already exists
                                            const existingIndex = items.findIndex(item => item.id === product.id);
                                            if (existingIndex >= 0) {
                                                // Increment quantity of existing item
                                                return items.map((item, index) =>
                                                    index === existingIndex
                                                        ? { ...item, qty: (item.qty || 1) + 1 }
                                                        : item
                                                );
                                            }
                                            // Add new item
                                            return [...items, { ...product, qty: 1 }];
                                        });
                                    }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
            {layoutMode === 'mobile' && (
                <button
                    onClick={handleClose}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        handleClose();
                    }}
                    style={{
                        width: '100%',
                        marginTop: '1rem',
                        background: theme?.colors?.secondary || '#888',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: 16,
                        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                        userSelect: 'none',
                    }}
                >
                    {t('back')}
                </button>
            )}
            </div>
        </div>
    );
}
